import type { JSX, ReactNode } from 'react';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core/react/i18n';
import { ComboBox } from '@change-corgi/design-system-legacy/components/forms';
import { Box } from '@change-corgi/design-system-legacy/layout';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/growth/petition-place.yml';

import { usePlaceWithDetailsField } from '../hooks/usePlaceWithDetailsField';
import type { GeoScope, PlaceWithDetails } from '../shared/types';

export type PlaceWithDetailsFieldProps = {
	label: string;
	placeholder?: string;
	hideLabel?: boolean;
	geoScope?: GeoScope;
	defaultPlaceValue?: PlaceWithDetails | null;
	onSelect?: (place: PlaceWithDetails | null) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	error?: ReactNode;
	inputLengthApiCallThreshold?: number;
};

function PlaceWithDetailsFieldComponent({
	geoScope,
	defaultPlaceValue = null,
	label,
	placeholder,
	hideLabel = false,
	onSelect,
	onFocus,
	onBlur,
	error,
	inputLengthApiCallThreshold,
}: PlaceWithDetailsFieldProps): JSX.Element {
	const { translate } = useI18n();

	const { placeInputValue, onPlaceInputChange, placeValue, onPlaceChange, placePredictions } = usePlaceWithDetailsField(
		{
			geoScope,
			defaultPlaceValue,
			onSelect,
			inputLengthApiCallThreshold,
		},
	);

	return (
		<Box mt={16}>
			<ComboBox
				id="petition-place"
				name="petition-place"
				label={label || translate('corgi.components.place.label')}
				placeholder={placeholder}
				hideLabel={hideLabel}
				inputValue={placeInputValue}
				onInputChange={(_, newInputValue) => onPlaceInputChange(newInputValue)}
				value={placeValue}
				onChange={(_, newValue) => onPlaceChange(newValue)}
				onFocus={onFocus}
				onBlur={onBlur}
				options={placePredictions}
				filterOptions={(_) => placePredictions}
				optionToString={(place) => place.displayName}
				// overriding the combobox popover zIndex to place the listbox
				// over the content on the page -- a fix for correct placement of the listbox
				// should be moved to the design system
				// eslint-disable-next-line @typescript-eslint/naming-convention
				sx={{ '& #petition-place-popover': { zIndex: 5 } }}
				error={error}
			/>
		</Box>
	);
}

export const PlaceWithDetailsField = withI18nPlaceholders(I18N_PLACEHOLDERS, PlaceWithDetailsFieldComponent);
